import React from 'react'
import { useTranslation } from 'react-i18next'
import { useTimer } from 'react-timer'
import Toast from 'react-toast'
import { NodeHtmlMarkdown } from 'node-html-markdown'
import { BeWizrCourse, RemoteImage } from '~/models'
import { bewizrCoursesStore, bewizrStore } from '~/stores'
import { observer } from '~/ui/component'
import { HBox, Hero, Markdown, Panel, PushButton, TextBlock, VBox } from '~/ui/components'
import { createUseStyles, layout } from '~/ui/styling'
import { BeWizrSection } from '../bewizr'
import BeWizrCourseItemList from './BeWizrCourseItemList'
import BeWizrCourseProgressPanel from './BeWizrCourseProgressPanel'

export interface Props {
  course: BeWizrCourse
}

const BeWizrCourseView = observer('BeWizrCourseView', (props: Props) => {

  const [t] = useTranslation('bewizr-courses')

  const {course} = props

  const document = bewizrCoursesStore.courses.document(course.slug)
  const lastVisitedCourseItem = document.lastVisitedCourseItem

  const enrolled = document.enrolled
  const [enrolling, setEnrolling] = React.useState<boolean>(false)

  const mayView = bewizrStore.profile?.hasNonParticipantCourses

  const timer = useTimer()

  const enroll = React.useCallback(async () => {
    if (enrolling) { return }

    setEnrolling(true)

    const result = await timer.await(document.enroll())
    if (result?.status === 200) {
      await document.fetch()
      Toast.show({
        type:     'success',
        title:    t('enroll-or-view.notification.title', {course}),
        detail:   t('enroll-or-view.notification.detail', {course}),
        lifetime: 20_000,
      })
    }

    setEnrolling(false)
  }, [document, timer, enrolling, t, course])

  const expandedItemIndex = React.useMemo(() => {
    if (lastVisitedCourseItem == null || !enrolled) { return }
    return course.items?.findIndex(it => it === lastVisitedCourseItem || it.items?.includes(lastVisitedCourseItem))
  }, [course.items, lastVisitedCourseItem, enrolled])

  const description = React.useMemo(() => {
    if (course.descriptionHtml == null || course.descriptionHtml.length === 0) { return null }
    // A little bit ridiculous, but this way we can use the styling from the <Markdown/> component
    return NodeHtmlMarkdown.translate(course.descriptionHtml)
  }, [course.descriptionHtml])

  const image: RemoteImage | undefined = course.links.image == null ? undefined : {
    type: 'remote',
    url:   course.links.image,
  }

  //------
  // Rendering

  const $ = useStyles()

  function render() {
    return (
      <VBox classNames={$.BeWizrCourseView}>
        <Hero
          title={course.title}
          image={image}
          backgroundImage={image}
          compact
          children={renderDescription()}
        />
        <VBox align='center' padding={layout.padding.m}>
          {renderContent()}
        </VBox>
      </VBox>
    )
  }

  function renderDescription() {
    if (description == null) { return null }

    return (
      <Markdown>
        {description}
      </Markdown>
    )
  }

  function renderContent() {
    return (
      <VBox classNames={$.content} gap={layout.padding.xl} paddingVertical={layout.padding.l}>
        {enrolled ? (
          <BeWizrSection title={t('progress.overview.title')}>
            <BeWizrCourseProgressPanel
              slug={course.slug}
            />
          </BeWizrSection>
        ) : (
          <BeWizrSection title={t(`enroll-or-view.caption.enroll`)}>
            {renderEnrollOrView()}
          </BeWizrSection>
        )}
        {course.items != null && course.items.length > 0 && (
          <BeWizrSection title={t('items.title')}>
            <BeWizrCourseItemList
              items={course.items}
              expandedItemIndex={expandedItemIndex}
              interactive={enrolled || mayView}
              showStatus={enrolled}
            />
          </BeWizrSection>
        )}
      </VBox>
    )
  }

  function renderEnrollOrView() {
    const externalEnrollUrl = course.links.externalEnrollUrl
    return (
      <Panel padding={layout.padding.inline.l} gap={layout.padding.m}>
        <TextBlock small>
          {t(`enroll-or-view.instruction.${mayView ? 'may-enroll-or-view' : 'may-enroll'}`)}
        </TextBlock>
        <HBox gap={layout.padding.inline.m}>
          {externalEnrollUrl != null ? (
            <PushButton
              icon='link'
              caption={t('enroll-or-view.caption.enroll')}
              href={externalEnrollUrl}
              target='_blank'
              flex
            />
          ) : (
            <PushButton
              caption={t('enroll-or-view.caption.enroll')}
              working={enrolling}
              onTap={enroll}
              flex
            />
          )}
          {mayView && (
            <PushButton
              icon='eye'
              caption={t('enroll-or-view.caption.view')}
              href={course.links.continue}
              flex
            />
          )}
        </HBox>
      </Panel>

    )
  }

  return render()

})

export default BeWizrCourseView

const useStyles = createUseStyles({
  BeWizrCourseView: {
  },

  content: {
    width:    layout.contentWidth,
    maxWidth: '100%',
  },
})